import * as React from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {useQuery} from '@apollo/react-hooks';
import {destroy} from 'redux-form';

import {CloseButton} from 'shells/button/close-button/close-button';
import {MinimizeButton} from 'shells/button/minimize-button/minimize-button';
import {Dialog} from 'shells/dialog/dialog';
// @ts-expect-error - imported from .js/.jsx file
import {EmailIcon} from 'shells/icon/svg/email-icon';

import type {
    GetReplyableEmail as GetReplyableEmailQuery,
    GetReplyableEmailVariables as GetReplyableEmailQueryVariables,
} from 'nutshell-graphql-types';

import type {NutshellState} from '../../store';

import {closeReplyModal, enlargeReplyModal} from './email-reply-actions';
import {getIsReplyMinimized, getReplyToEmailId} from './email-reply-selectors';
import GET_REPLYABLE_EMAIL from './sender/graphql/queries/get-replyable-email.graphql';
import {FORM_NAME} from './sender/types';

import styles from './minimized-email.module.css';

export function MinimizedReply() {
    const isMinimized = useSelector((state: NutshellState) => getIsReplyMinimized(state));
    const replyToEmailId = useSelector((state: NutshellState) => getReplyToEmailId(state));
    const dispatch = useDispatch();

    const [isConfirmingClose, setIsConfirmingClose] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const {data} = useQuery<GetReplyableEmailQuery, GetReplyableEmailQueryVariables>(
        GET_REPLYABLE_EMAIL,
        {
            variables: {emailId: replyToEmailId || ''},
            skip: !isMinimized || !replyToEmailId,
            fetchPolicy: 'cache-first',
        }
    );

    if (!isMinimized || !replyToEmailId) {
        return null;
    }

    const replyToDisplay = data?.replyEmail?.replyToAddress.display || 'email';

    const handleEnlarge = () => {
        dispatch(enlargeReplyModal());
    };

    const handleCloseClick = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        setIsConfirmingClose(true);
    };

    const handleCloseConfirm = () => {
        setIsConfirmingClose(false);
        dispatch(closeReplyModal());
        dispatch(destroy(FORM_NAME));
    };

    const handleCloseCancel = () => {
        setIsConfirmingClose(false);
    };

    return (
        <div ref={containerRef} onClick={handleEnlarge} className={styles['minimized-email']}>
            <EmailIcon size={21} />
            <div className={styles.summary}>
                <div className={styles.subject}>Reply</div>
                <span>To: {replyToDisplay}</span>
            </div>
            <div className={styles.buttons}>
                <MinimizeButton tooltip='Enlarge' onClick={handleEnlarge} />
                <CloseButton tooltip='Discard' onClick={handleCloseClick} />
            </div>
            {isConfirmingClose && (
                <Dialog
                    anchor={containerRef.current}
                    location='top'
                    question='Are you sure you want to discard this reply?'
                    onPrimary={handleCloseConfirm}
                    onSecondary={handleCloseCancel}
                />
            )}
        </div>
    );
}
